import { AI_API_KEY, BASE_API_URL } from 'src/app/environment';
import { Review } from '../types/types';
import { experimentalBuildReviewReplyPrompt } from '../prompts/review';

/**
 * Central improvements notes:
 * - Single model instance helper (lazy) with early API key validation
 * - Strongly typed custom error objects for upstream differentiation
 * - Small utility helpers for prompt assembly, JSON parsing resilience, hashtag sanitation & timeouts
 * - No breaking changes to existing exported function names or signatures
 */

import { GoogleGenerativeAI } from '@google/generative-ai';
import { axiosInstance } from '../client';
import {
  GeneratedSocialPostResult,
  GenerateSocialPostRequest,
} from '../types/social-types';
import { BusinessLocation } from '../types/business-types';

// ------------------------------
// Error Types
// ------------------------------
export class AIServiceError extends Error {
  readonly kind:
    | 'config'
    | 'network'
    | 'model'
    | 'parse'
    | 'timeout'
    | 'unknown';
  readonly original?: unknown;
  constructor(
    message: string,
    kind: 'config' | 'network' | 'model' | 'parse' | 'timeout' | 'unknown',
    original?: unknown,
  ) {
    super(message);
    this.name = 'AIServiceError';
    this.kind = kind;
    this.original = original;
  }
}

// ------------------------------
// Lazy Model Access
// ------------------------------
let _genAI: GoogleGenerativeAI | null = null;
const getModel = (modelName = 'gemini-2.5-flash-lite') => {
  if (!_genAI) {
    if (!AI_API_KEY || AI_API_KEY.trim().length < 10) {
      throw new AIServiceError('Missing or invalid AI API key', 'config');
    }
    _genAI = new GoogleGenerativeAI(AI_API_KEY.trim());
  }
  return _genAI.getGenerativeModel({ model: modelName });
};

// ------------------------------
// Utility Helpers
// ------------------------------
const withTimeout = async <T>(p: Promise<T>, ms = 30_000): Promise<T> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), ms);
  try {
    // NOTE: The underlying SDK may not yet support AbortController; we still race manually.
    const result = await Promise.race([
      p,
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new AIServiceError('AI request timed out', 'timeout')),
          ms,
        ),
      ),
    ]);
    return result as T;
  } finally {
    clearTimeout(timeout);
  }
};

const generateReviewReply = async (
  review: Review,
  location?: BusinessLocation,
  guidelines?: string,
  priorityKeywords?: string[],
): Promise<string> => {
  try {
    const model = getModel();
    const prompt = experimentalBuildReviewReplyPrompt(
      review,
      location,
      guidelines,
      priorityKeywords,
    );

    const result = await withTimeout(model.generateContent([prompt]));
    const generatedReply = result?.response?.text()?.trim() || '';

    // Optional: Strip unwanted prefixes or markdown
    return generatedReply
      .replace(/^["'`]+|["'`]+$/g, '') // remove stray quotes
      .replace(/^([Hh]ere'?s a possible response:?)/, '')
      .trim();
  } catch (error) {
    throw error instanceof AIServiceError
      ? error
      : new AIServiceError('Failed to generate review reply', 'unknown', error);
  }
};

export { generateReviewReply };

// Server-side Vertex AI variant: calls our Functions API for content generation
export const generateSocialPostViaVertex = async (
  req: GenerateSocialPostRequest,
): Promise<GeneratedSocialPostResult> => {
  const url = `${BASE_API_URL}/ai/generate-social`;
  try {
    const { data } = await axiosInstance.post(url, req, {
      headers: { 'Content-Type': 'application/json' },
    });

    if (!data?.success) {
      throw new AIServiceError('Server returned failure', 'model');
    }
    return data;
  } catch (error) {
    throw error instanceof AIServiceError
      ? error
      : new AIServiceError(
          'Failed calling Vertex AI endpoint',
          'network',
          error,
        );
  }
};
